import { GlobalFilterState } from './GlobalFilterState';
import {
  DatePicker,
  DatePickerChangeEvent,
  DateTimePicker,
  DateTimePickerChangeEvent,
  MultiViewCalendarProps
} from '@progress/kendo-react-dateinputs';
import { RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { GEAR_FIELD_TYPE } from '../../../../global/enums/GearFieldType';
import { useSearchContext } from '../../../../context/search/SearchContext';
import { CompositeFilterDescriptor, FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { useDocumentsContext } from '../../../../context/documents/DocumentsContext';
import { GearFilterRequest } from '../../../../Model/GearFilterRequest';
import React, { useRef, useState } from 'react';
import { CustomDateTimeInput } from './CustomDateTimeInput';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { UnifiedCalendar } from './dateTimeRange/UnifiedDateCalendar';
import { CustomDatePopup } from './CustomDatePopup';
import { useGlobalizationContext } from '../../../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../../../context/globalization/useTranslation';
import { getGearDateFilterFormat, getGearDateTimeFilterFormat } from '../../../../helpers/DayJsHelper';

interface GroupedDateGlobalFilterProps {
  dateFiltersState: GlobalFilterState[];
}

const PICKER_START_ID = 'dateTimePickerStart';
const PICKER_END_ID = 'dateTimePickerEnd';

const DUMMY_VALUE = 'x'; // dummy value to make floating label be on top from the start

export const GroupedDateGlobalFilter: React.FC<GroupedDateGlobalFilterProps> = props => {
  const { dateFiltersState } = props;
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { queryRequest, setQueryRequest, compositeFilterDescriptor, setCompositeFilterDescriptor } = useSearchContext();
  const { setSelectedDocuments } = useDocumentsContext();
  const [fieldSelected, setFieldSelected] = useState<GlobalFilterState | undefined>(
    dateFiltersState.length > 0 ? dateFiltersState[0] : undefined
  );
  const dateTimeStartRef = useRef<any>(null);
  const dateTimeEndRef = useRef<any>(null);
  const dateStartRef = useRef<any>(null);
  const dateEndRef = useRef<any>(null);

  const [startPickerOpen, setStartPickerOpen] = React.useState(false);
  const [endPickerOpen, setEndPickerOpen] = React.useState(false);

  const handleFieldChange = (event: RadioButtonChangeEvent) => {
    const newFieldSelected = props.dateFiltersState.find(filter => filter.id === event.value);
    setFieldSelected(newFieldSelected);

    if (queryRequest && setQueryRequest && setCompositeFilterDescriptor && fieldSelected) {
      const newFilters =
        queryRequest.filters?.filter(
          filter => !props.dateFiltersState.map(state => state.name).includes(filter.fieldName)
        ) || [];
      setQueryRequest({
        ...queryRequest,
        filters: newFilters
      });
      if (setSelectedDocuments) {
        setSelectedDocuments([]);
      }
    }
  };

  if (!fieldSelected) {
    return <></>;
  }

  const handleChange = (event: DateTimePickerChangeEvent | DatePickerChangeEvent) => {
    if (!event.target.props) {
      return;
    }
    const newValue = event.value as Date;
    const pickerId = event.target.props.id as typeof PICKER_START_ID | typeof PICKER_END_ID;

    if (pickerId === PICKER_START_ID) {
      setStartPickerOpen(true);
    } else if (pickerId === PICKER_END_ID) {
      setEndPickerOpen(true);
    }
    if (queryRequest && setQueryRequest && setCompositeFilterDescriptor) {
      const otherFieldFilters = queryRequest.filters?.filter(
        filter =>
          filter.fieldName !== fieldSelected.name ||
          (filter.fieldName === fieldSelected.name &&
            pickerId === PICKER_START_ID &&
            filter.operator === FilterOperator.LessThanOrEqual) ||
          (filter.fieldName === fieldSelected.name &&
            pickerId === PICKER_END_ID &&
            filter.operator === FilterOperator.GreaterThanOrEqual)
      );
      if (compositeFilterDescriptor) {
        const newCompositeFilterDescriptor: CompositeFilterDescriptor = {
          ...compositeFilterDescriptor,
          filters: (compositeFilterDescriptor.filters as CompositeFilterDescriptor[]).filter(filter1 =>
            (filter1.filters as FilterDescriptor[]).some(filter2 => filter2.field !== fieldSelected.name)
          )
        };
        setCompositeFilterDescriptor(newCompositeFilterDescriptor);
      }

      const newFilters = otherFieldFilters || [];
      if (newValue && (Array.isArray(newValue) ? newValue.length > 0 : true)) {
        switch (pickerId) {
          case PICKER_START_ID: {
            const startFilter: GearFilterRequest = {
              fieldName: fieldSelected.name,
              operator: FilterOperator.GreaterThanOrEqual,
              values: [
                fieldSelected.type === GEAR_FIELD_TYPE.DATETIME
                  ? getGearDateTimeFilterFormat(newValue)
                  : getGearDateFilterFormat(newValue)
              ],
              fieldType: GEAR_FIELD_TYPE.DATE,
              isGlobal: true
            } as GearFilterRequest;
            newFilters.push(startFilter);
            break;
          }
          case PICKER_END_ID: {
            const endFilter: GearFilterRequest = {
              fieldName: fieldSelected.name,
              operator: FilterOperator.LessThanOrEqual,
              values: [
                fieldSelected.type === GEAR_FIELD_TYPE.DATETIME
                  ? getGearDateTimeFilterFormat(newValue)
                  : getGearDateFilterFormat(newValue)
              ],
              fieldType: GEAR_FIELD_TYPE.DATE,
              isGlobal: true
            } as GearFilterRequest;
            newFilters.push(endFilter);
            break;
          }
          default:
            break;
        }
      }
      setQueryRequest({
        ...queryRequest,
        filters: newFilters
      });
      if (setSelectedDocuments) {
        setSelectedDocuments([]);
      }
    }
  };

  const handlePopupOpen = (pickerId: string) => {
    if (pickerId === PICKER_START_ID) {
      setStartPickerOpen(true);
      setEndPickerOpen(false);
    } else {
      setEndPickerOpen(true);
      setStartPickerOpen(false);
    }
  };

  const handlePopupClose = (pickerId: string) => {
    if (pickerId === PICKER_START_ID) {
      setStartPickerOpen(false);
    } else {
      setEndPickerOpen(false);
    }
  };

  const currentValueStart = queryRequest?.filters?.find(
    f => f.fieldName === fieldSelected.name && f.isGlobal && f.operator === FilterOperator.GreaterThanOrEqual
  )?.values as string[] | undefined;
  const currentValueEnd = queryRequest?.filters?.find(
    f => f.fieldName === fieldSelected.name && f.isGlobal && f.operator === FilterOperator.LessThanOrEqual
  )?.values as string[] | undefined;

  const todayEndOfTheDay = new Date(new Date().setHours(23, 59, 59, 999));

  const currentTranslatedField = props.dateFiltersState.find(filter => filter.id === fieldSelected.id);
  const fieldDisplayName = currentTranslatedField ? currentTranslatedField.displayName : fieldSelected.displayName;

  return (
    <div className="dateTimeRangeFilter">
      {fieldSelected.type === GEAR_FIELD_TYPE.DATETIME ? (
        <>
          <FloatingLabel
            editorId={PICKER_START_ID}
            editorValue={DUMMY_VALUE}
            label={`${fieldDisplayName} (${translate('daterangepicker.start')})`}
          >
            <DateTimePicker
              className="volvoDatePicker"
              ref={dateTimeStartRef}
              id={PICKER_START_ID}
              value={currentValueStart && currentValueStart.length > 0 ? new Date(currentValueStart[0]) : null}
              popup={CustomDatePopup}
              show={startPickerOpen}
              onOpen={() => handlePopupOpen(PICKER_START_ID)}
              onClose={() => handlePopupClose(PICKER_START_ID)}
              calendar={calendarProps => (
                <UnifiedCalendar
                  calendarProps={calendarProps}
                  dateFiltersState={props.dateFiltersState}
                  fieldSelected={fieldSelected}
                  handleFieldChange={handleFieldChange}
                />
              )}
              onChange={handleChange}
            />
          </FloatingLabel>
          <FloatingLabel
            editorId={PICKER_END_ID}
            editorValue={DUMMY_VALUE}
            label={`${fieldDisplayName} (${translate('daterangepicker.end')})`}
          >
            <DateTimePicker
              className="volvoDatePicker"
              key={currentValueEnd && currentValueEnd.length > 0 ? 'endDefined' : 'endUndefined'}
              ref={dateTimeEndRef}
              id={PICKER_END_ID}
              value={currentValueEnd && currentValueEnd.length > 0 ? new Date(currentValueEnd[0]) : undefined}
              valid={true}
              popup={CustomDatePopup}
              show={endPickerOpen}
              onOpen={() => handlePopupOpen(PICKER_END_ID)}
              onClose={() => handlePopupClose(PICKER_END_ID)}
              defaultValue={todayEndOfTheDay}
              dateInput={inputProps => (
                <CustomDateTimeInput
                  {...inputProps}
                  isDefaultValue={!currentValueEnd || currentValueEnd.length === 0}
                />
              )}
              calendar={calendarProps => (
                <UnifiedCalendar
                  calendarProps={calendarProps}
                  dateFiltersState={props.dateFiltersState}
                  fieldSelected={fieldSelected}
                  handleFieldChange={handleFieldChange}
                />
              )}
              onChange={handleChange}
            />
          </FloatingLabel>
        </>
      ) : (
        <>
          <FloatingLabel
            editorId={PICKER_START_ID}
            editorValue={DUMMY_VALUE}
            label={`${fieldDisplayName} (${translate('daterangepicker.start')})`}
          >
            <DatePicker
              className="volvoDatePicker"
              ref={dateStartRef}
              id={PICKER_START_ID}
              value={currentValueStart && currentValueStart.length > 0 ? new Date(currentValueStart[0]) : null}
              show={startPickerOpen}
              popupSettings={{ animate: false }}
              onOpen={() => handlePopupOpen(PICKER_START_ID)}
              onClose={() => handlePopupClose(PICKER_START_ID)}
              calendar={(calendarProps: MultiViewCalendarProps) => (
                <UnifiedCalendar
                  calendarProps={calendarProps}
                  dateFiltersState={props.dateFiltersState}
                  fieldSelected={fieldSelected}
                  handleFieldChange={handleFieldChange}
                />
              )}
              onChange={handleChange}
            />
          </FloatingLabel>
          <FloatingLabel
            editorId={PICKER_END_ID}
            editorValue={DUMMY_VALUE}
            label={`${fieldDisplayName} (${translate('daterangepicker.end')})`}
          >
            <DatePicker
              className="volvoDatePicker"
              ref={dateEndRef}
              id={PICKER_END_ID}
              value={currentValueEnd && currentValueEnd.length > 0 ? new Date(currentValueEnd[0]) : null}
              show={endPickerOpen}
              popupSettings={{ animate: false }}
              onOpen={() => handlePopupOpen(PICKER_END_ID)}
              onClose={() => handlePopupClose(PICKER_END_ID)}
              calendar={(calendarProps: MultiViewCalendarProps) => (
                <UnifiedCalendar
                  calendarProps={calendarProps}
                  dateFiltersState={props.dateFiltersState}
                  fieldSelected={fieldSelected}
                  handleFieldChange={handleFieldChange}
                />
              )}
              onChange={handleChange}
            />
          </FloatingLabel>
        </>
      )}
    </div>
  );
};
